/**
 * @file 学びの計画の面 — 取り込んだ計画を切り替え、カリキュラムから増やし、終えたものを戻す。
 *
 * @what: 学ぶ人が取り込んだ計画をタブで並べ、押して切り替える。選んだ計画は参考書ルートの見せ方
 *   (科目 × 難易度 / スケジュール) をそのカリキュラムに絞って出す。「カリキュラムを選ぶ」で対話を
 *   開き、家族にあるカリキュラムから増やす。終えた計画は下にまとめ、いつでも戻せる。
 * @why: 計画の面に出すのは取り込んだ計画だけにする。カリキュラムの一覧の段では全部見えてよい
 *   (代表の決定、2026-09-11)。※なぜならルートに学習者の種別を持たせると軸が増え、取り込んだ
 *   ものだけを見せれば同じ結果になるためである (設計 §5.5)。
 *
 *   進め方は学ぶ人に任せ、「今日触るのは1つ」のような制約は画面に持たせない (代表の指摘、
 *   2026-09-11)。終えるのは元に戻せる操作にし、押し間違えても設定を失わないようにする。
 * @failure: 計画を読めなければ1行で知らせる。学ぶ人を選んでいなければ案内だけを出す。
 */

import { useMemo, useState } from "react";
import {
  countRouteBooks,
  groupRoutesByTarget,
  latestTextbookRoutes,
  type StudyTextbookRoute,
  type StudyTextbookRouteCareer,
} from "../api/textbookRoutes";
import type { StudyLearningPlan, StudyLearningPlanInput } from "../api/learningPlans";
import type { StudySource } from "../api/sources";
import type { TextbookRoutesState } from "../hooks/useTextbookRoutes";
import { useLearningPlans } from "../hooks/useLearningPlans";
import { CareersPanel } from "./CareersPanel";
import { StudyDialog } from "./StudyDialog";
import type { ActivityByIsbn } from "../model/plannedRanges";
import { TextbookRoutePanel } from "./TextbookRoutePanel";
import "./study.css";

/**
 * 計画1件が指すカリキュラムのルートを返す。
 *
 * @what: 系列の id が一致するルートを集める。同じ対象に理系編と文系編のように複数の系列があっても、
 *   計画が指すのは1つの系列だけである。
 * @why: 計画の面は取り込んだカリキュラムだけを出すので、ルートの一覧をここで絞る。
 */
function routesOfPlan(
  routes: readonly StudyTextbookRoute[],
  plan: StudyLearningPlan,
): StudyTextbookRoute[] {
  return routes.filter((r) => r.series_id === plan.route_series_id);
}

/**
 * 計画が指すカリキュラムの働き方を返す。
 *
 * @what: 同じ系列の複数のルート (年度) が持つ働き方を、名前で重ねて1つにする。
 * @why: 働き方はルートに付くが、計画からは1つの並びとして見えるほうが読みやすい。
 */
function careersOfPlan(
  routes: readonly StudyTextbookRoute[],
  plan: StudyLearningPlan,
): StudyTextbookRouteCareer[] {
  const out: StudyTextbookRouteCareer[] = [];
  for (const r of routesOfPlan(routes, plan)) {
    for (const c of r.careers ?? []) {
      if (!out.some((x) => x.name === c.name)) out.push(c);
    }
  }
  return out;
}

/** 計画に出てくるカリキュラムの名前を返す (ルートが消えていれば対象の文字列を使う)。 */
function planTitle(routes: readonly StudyTextbookRoute[], plan: StudyLearningPlan): string {
  const mine = routesOfPlan(routes, plan);
  return mine[0]?.target ?? plan.target;
}

/**
 * いまの設定をそのまま持った保存の入れ物を作る。
 *
 * @what: 目標・期限・起点・科目を計画から引き継ぎ、状態だけを差し替えられる形にする。
 * @why: 終えるときと戻すときに設定を消さないためである。※なぜなら保存は全置換なので、送らなかった
 *   項目は消えるからである。
 */
function inputFromPlan(
  plan: StudyLearningPlan,
  status: StudyLearningPlan["status"],
): StudyLearningPlanInput {
  const input: StudyLearningPlanInput = { target: plan.target, status, subjects: plan.subjects };
  if (plan.goal_school !== undefined) input.goal_school = plan.goal_school;
  if (plan.goal_deviation !== undefined) input.goal_deviation = plan.goal_deviation;
  if (plan.exam_date !== undefined) input.exam_date = plan.exam_date;
  if (plan.start_date !== undefined) input.start_date = plan.start_date;
  // 終えた日はサーバが受け持つ。戻すときは空にして送り、終えるときは何も送らない。
  // ※なぜなら backend の Validate が「done なら終えた日が必須、active なら空」を守り、PUT は
  // done で日付が空のときに今日を入れるためである。画面が日付を作ると2か所で決めることになる。
  if (status === "active") input.ended_at = "";
  return input;
}

export function LearningPlansPanel({
  routesState,
  display,
  familyId,
  learnerPersonId,
  learnerLabel,
  sources,
  showCareers = false,
  activity,
}: {
  routesState: TextbookRoutesState;
  /** 選んだ計画をどの見せ方で出すか。 */
  display: "grid" | "schedule";
  familyId: string;
  learnerPersonId: string | null;
  learnerLabel: string;
  sources?: readonly StudySource[] | null;
  /**
   * 働く姿の節を出すかを渡す。おとなの学ぶ人のときだけ true にする (代表の決定、2026-09-11)。
   * 年齢では切らない。※なぜなら発達の面で年齢の軸を出さない原則があり、既にある養育者かどうかの
   * 区分で同じ結果になるためである。
   */
  showCareers?: boolean;
  /** 本 (ISBN) ごとの触れた日 (時系列の表に重ねる印と、今週の実績に使う)。 */
  activity?: ActivityByIsbn;
}) {
  const routes = useMemo(
    () => (routesState.status === "ready" ? latestTextbookRoutes(routesState.routes) : []),
    [routesState],
  );
  const catalog = useMemo(() => groupRoutesByTarget(routes), [routes]);
  const plans = useLearningPlans(familyId, learnerPersonId, learnerPersonId !== null);

  const active = useMemo(
    () =>
      plans.state.status === "ready" ? plans.state.plans.filter((p) => p.status === "active") : [],
    [plans.state],
  );
  const finished = useMemo(
    () =>
      plans.state.status === "ready" ? plans.state.plans.filter((p) => p.status === "done") : [],
    [plans.state],
  );

  const [choice, setChoice] = useState<string | null>(null);
  // 選んでいない・終えた計画を選んでいたら先頭に倒す。
  const selected = active.find((p) => p.id === choice) ?? active[0] ?? null;
  const [catalogOpen, setCatalogOpen] = useState(false);
  // 終えるときは確かめる。※なぜなら押し間違いで計画が一覧から消え、戻し方が分からなくなるためである
  // (代表の指摘、2026-09-11)。
  const [finishing, setFinishing] = useState<StudyLearningPlan | null>(null);

  const takeIn = async (target: string, seriesId: string): Promise<void> => {
    const ok = await plans.save(seriesId, { target, subjects: {} });
    if (ok) {
      setChoice(`${learnerPersonId ?? ""}__${seriesId}`);
      setCatalogOpen(false);
    }
  };

  // 終えるのと戻すのは、いまの設定をそのまま送って状態だけを変える (設定を消さずに残す)。
  const setStatus = async (
    plan: StudyLearningPlan,
    status: StudyLearningPlan["status"],
  ): Promise<void> => {
    const ok = await plans.save(plan.route_series_id, inputFromPlan(plan, status));
    if (ok && status === "active") {
      setChoice(plan.id);
      setCatalogOpen(false);
    }
  };

  if (learnerPersonId === null)
    return (
      <section className="study-section">
        <h2 className="study-section-title">
          <span aria-hidden="true">🧭</span> 学びの計画
        </h2>
        <p className="study-muted">学ぶ人を選ぶと、その人の計画が出ます。</p>
      </section>
    );

  return (
    <section className="study-section">
      <h2 className="study-section-title">
        <span aria-hidden="true">🧭</span> 学びの計画
      </h2>
      <p className="study-section-help">
        {learnerLabel}が取り込んだカリキュラムの計画です。何本でも並行して持てます。進め方は自由で、
        少しずつ進めてもかまいません。
      </p>
      {plans.state.status === "loading" ? (
        <p role="status" className="study-muted study-panel-loading">
          計画を読み込んでいます...
        </p>
      ) : plans.state.status === "error" ? (
        <p role="alert" className="study-error">
          計画を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      ) : (
        <>
          {plans.saveError !== null ? (
            <p role="alert" className="study-error">
              {plans.saveError}
            </p>
          ) : null}
          <div className="study-plan-switch">
            {active.length > 0 ? (
              <div className="study-plan-tabs" role="tablist" aria-label="取り込んだ計画">
                {active.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={p.id === selected?.id}
                    className="study-plan-tab"
                    onClick={() => setChoice(p.id)}
                  >
                    <span className="study-plan-tab-name">{planTitle(routes, p)}</span>
                    <span className="study-plan-tab-meta">
                      {p.exam_date ? `${p.exam_date}まで` : "期限なし"}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="study-muted">
                まだ計画がありません。「カリキュラムを選ぶ」から取り込むと、ここに並びます。
              </p>
            )}
            <button
              type="button"
              className="study-primary-btn study-plan-add"
              onClick={() => setCatalogOpen(true)}
            >
              カリキュラムを選ぶ
            </button>
          </div>
          {catalogOpen ? (
            <StudyDialog
              title="カリキュラムを選ぶ"
              labelledBy="study-curriculum-dialog-title"
              onClose={() => setCatalogOpen(false)}
            >
              <CurriculumCatalog
                catalog={catalog}
                routesState={routesState}
                taken={plans.state.plans}
                saving={plans.saving}
                onTakeIn={(target, seriesId) => void takeIn(target, seriesId)}
                onReopen={(plan) => void setStatus(plan, "active")}
              />
            </StudyDialog>
          ) : null}
          {selected !== null && showCareers ? (
            <CareersPanel careers={careersOfPlan(routes, selected)} />
          ) : null}
          {selected !== null ? (
            <TextbookRoutePanel
              routesState={{ status: "ready", routes: routesOfPlan(routes, selected) }}
              display={display}
              familyId={familyId}
              learnerPersonId={learnerPersonId}
              learnerLabel={learnerLabel}
              sources={sources ?? null}
              {...(activity ? { activity } : {})}
              store={{
                state: { status: "ready", plan: selected },
                save: (input) => plans.save(selected.route_series_id, input),
                saving: plans.saving,
                saveError: plans.saveError,
              }}
            />
          ) : null}
          {selected !== null ? (
            <p className="study-plan-finish">
              <button
                type="button"
                className="study-link-btn"
                onClick={() => setFinishing(selected)}
                disabled={plans.saving}
              >
                この計画を終える
              </button>
            </p>
          ) : null}
          {finished.length > 0 ? (
            <div className="study-plan-finished">
              <h3 className="study-plan-finished-title">終えた計画</h3>
              <ul className="study-plan-finished-list">
                {finished.map((p) => (
                  <li key={p.id}>
                    <span>
                      {planTitle(routes, p)}
                      {p.ended_at ? `（${p.ended_at}）` : ""}
                    </span>
                    <button
                      type="button"
                      className="study-ghost-btn"
                      onClick={() => void setStatus(p, "active")}
                      disabled={plans.saving}
                    >
                      戻す
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {finishing !== null ? (
            <StudyDialog
              title="この計画を終えますか"
              labelledBy="study-plan-finish-dialog-title"
              onClose={() => setFinishing(null)}
            >
              <p>
                「{planTitle(routes, finishing)}」を終えると、上の切り替えから外れます。目標・期限・
                科目の設定は残るので、下の「終えた計画」からいつでも戻せます。
              </p>
              <div className="study-dialog-actions">
                <button
                  type="button"
                  className="study-ghost-btn"
                  onClick={() => setFinishing(null)}
                >
                  やめる
                </button>
                <button
                  type="button"
                  className="study-primary-btn"
                  onClick={() => {
                    const target = finishing;
                    setFinishing(null);
                    void setStatus(target, "done");
                  }}
                  disabled={plans.saving}
                >
                  終える
                </button>
              </div>
            </StudyDialog>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * カリキュラムの一覧を出す。
 *
 * @what: 家族にあるカリキュラムを対象ごとに全部並べる。取り込んでいないものには「取り込む」、終えた
 *   ものには「戻す」を出す。進めている計画のものは印だけにする。
 * @why: 一覧の段では学ぶ人の種別で絞らない (代表の決定、2026-09-11)。終えたカリキュラムをここから
 *   選び直せるようにするのは、取り込み直しが実質「戻す」であり、設定を引き継ぎたいためである
 *   (代表の指摘、2026-09-11)。
 */
function CurriculumCatalog({
  catalog,
  routesState,
  taken,
  saving,
  onTakeIn,
  onReopen,
}: {
  catalog: { target: string; routes: StudyTextbookRoute[] }[];
  routesState: TextbookRoutesState;
  taken: readonly StudyLearningPlan[];
  saving: boolean;
  onTakeIn: (target: string, seriesId: string) => void;
  onReopen: (plan: StudyLearningPlan) => void;
}) {
  if (routesState.status === "loading")
    return (
      <p role="status" className="study-muted study-panel-loading">
        カリキュラムを読み込んでいます...
      </p>
    );
  if (routesState.status === "error")
    return (
      <p role="alert" className="study-error">
        カリキュラムを読み込めませんでした。時間を置いて再読み込みしてください。
      </p>
    );
  if (catalog.length === 0)
    return (
      <p className="study-muted">
        まだカリキュラムを取り込んでいません。取り込むと、ここから選べるようになります。
      </p>
    );
  return (
    <ul className="study-curriculum-catalog" aria-label="カリキュラムの一覧">
      {catalog.map((g) =>
        g.routes.map((r) => {
          const mine = taken.find((p) => p.route_series_id === r.series_id);
          return (
            <li key={r.id} className="study-curriculum-item">
              <div className="study-curriculum-body">
                <b>{r.title}</b>
                <span className="study-muted">
                  {g.target}・{r.subjects.length}科目・{countRouteBooks(r)}冊
                  {r.edition ? `・${r.edition}` : ""}
                </span>
              </div>
              {mine === undefined ? (
                <button
                  type="button"
                  className="study-ghost-btn"
                  onClick={() => onTakeIn(r.target, r.series_id)}
                  disabled={saving}
                >
                  取り込む
                </button>
              ) : mine.status === "done" ? (
                <button
                  type="button"
                  className="study-ghost-btn"
                  onClick={() => onReopen(mine)}
                  disabled={saving}
                >
                  戻す
                </button>
              ) : (
                <span className="study-muted">取り込み済み</span>
              )}
            </li>
          );
        }),
      )}
    </ul>
  );
}
